import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AlertController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { map as lodashMap } from 'lodash-es';
import { Observable, tap } from 'rxjs';

import * as fromWalkingFarmActions from '../../../../store/actions/walking-farm.actions';
import * as fromWalkingFarmSelectors from '../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmAlert, WalkingFarmUnreadAlert } from '../../../../typings/walking-farm';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-alerts',
  standalone: false,
  templateUrl: './alerts.component.html'
})
export class AlertsPageComponent {
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private alertController = inject(AlertController);

  walkingFarmId: string | null;

  alertsFarmName$: Observable<string | null>;
  alertsEnabled$: Observable<boolean>;
  alertLastDate$: Observable<Date | null>;
  alertsList$: Observable<WalkingFarmAlert[]>;
  alertsLoaded$: Observable<boolean>;
  unreadAlerts$: Observable<WalkingFarmAlert[]>;

  areAlertsEnabled = false;
  isAlertsToggleConfirmed = false;

  defaultBackHref: string | undefined;
  defaultHref: string;

  private unreadAlerts: WalkingFarmUnreadAlert[] = [];

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [this.defaultBackHref ?? '', 'walking-farm'].join('/');

    this.walkingFarmId = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.loadAlerts({
          payload: {
            id: this.walkingFarmId
          }
        })
      );
    }
    this.alertsFarmName$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsFarmName));
    this.alertsEnabled$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsEnabled)).pipe(
      tap((areAlertsEnabled) => {
        this.areAlertsEnabled = areAlertsEnabled;
        this.isAlertsToggleConfirmed = false;
      })
    );
    this.alertLastDate$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsAlertLastDate));
    this.alertsList$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsList));
    this.alertsLoaded$ = this.store.pipe(select(fromWalkingFarmSelectors.selectAlertsLoaded));
    this.unreadAlerts$ = this.store.pipe(select(fromWalkingFarmSelectors.selectUnreadAlertsList)).pipe(
      tap((unreadAlerts) => {
        this.unreadAlerts = lodashMap(unreadAlerts, (alert) => ({
          alertId: alert.id,
          propertyId: `${alert.propertyNumber}`
        }));
      })
    );
  }

  alertsListTrackBy(_index: number, alert: WalkingFarmAlert) {
    return alert.id;
  }

  async confirmToggle(event: MouseEvent) {
    if (this.areAlertsEnabled && !this.isAlertsToggleConfirmed) {
      const { target } = event;
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
      const alert = await this.alertController.create({
        buttons: [
          {
            role: 'cancel',
            text: 'Cancel'
          },
          {
            role: 'confirm',
            text: 'Confirm'
          }
        ],
        header: 'Disable Farm Alerts',
        message: 'Are you sure? By turning the alerts off you will no longer receive information updates for this farm.'
      });

      await alert.present();

      const { role } = await alert.onDidDismiss();
      if (role === 'confirm') {
        this.isAlertsToggleConfirmed = true;
        target?.dispatchEvent(event);
      }
    }
  }

  async toggleFarmAlertsEnabled(event: CustomEvent) {
    if (this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.toggleAlertsEnabled({
          payload: {
            enabled: event.detail.checked,
            id: this.walkingFarmId
          }
        })
      );
    }
  }

  markAlertAsRead(alert: WalkingFarmAlert) {
    if (!alert.isRead && this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.markAlertsAsRead({
          payload: {
            farmId: this.walkingFarmId,
            unreadAlerts: [
              {
                alertId: alert.id,
                propertyId: `${alert.propertyNumber}`
              }
            ]
          }
        })
      );
    }
  }

  markAllAlertsAsRead() {
    if (this.unreadAlerts.length && this.walkingFarmId) {
      this.store.dispatch(
        fromWalkingFarmActions.markAlertsAsRead({
          payload: {
            farmId: this.walkingFarmId,
            unreadAlerts: this.unreadAlerts
          }
        })
      );
    }
  }
}
